import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { api } from "../lib/api";
import {
  X, Upload, FileSpreadsheet, Download, AlertCircle, CheckCircle2,
  AlertTriangle, ChevronRight, ChevronLeft, Trash2, FileX, Table as TableIcon,
} from "lucide-react";

// Spec-mandated column header → internal field map (Playmaker template is canonical)
const CLIENT_COLUMN_MAP = {
  "First name":              "first_name",
  "Last name":               "last_name",
  "Date of birth":           "date_of_birth",
  "Nationality":             "nationality",
  "Second nationality":      "second_nationality",
  "Position":                "position",
  "Preferred foot":          "preferred_foot",
  "Height (cm)":             "height_cm",
  "Weight (kg)":             "weight_kg",
  "Current club":            "current_club",
  "League":                  "league",
  "Club country":            "club_country",
  "Squad number":            "squad_number",
  "Contract end date":       "contract_end",
  "Weekly gross wage":       "weekly_gross_wage",
  "Wage currency":           "wage_currency",
  "Agent commission %":      "agent_commission_pct",
  "Image rights %":          "image_rights_pct",
  "Release clause":          "release_clause",
  "Signing-on fee":          "signing_fee",
  "Transfermarkt URL":       "transfermarkt_url",
  "Wyscout player ID":       "wyscout_id",
  "SciSports player ID":     "scisports_id",
  "Player mobile":           "player_mobile",
  "Player email":            "player_email",
  "Player WhatsApp":         "player_whatsapp",
  "Preferred language":      "preferred_language",
  "Family contact 1 name":   "family_contact_1_name",
  "Family contact 1 role":   "family_contact_1_role",
  "Family contact 1 phone":  "family_contact_1_phone",
  "Lawyer name":             "lawyer_name",
  "Law firm":                "law_firm",
  "Agent notes":             "agent_notes",
  "Assigned agent email":    "assigned_agent_email",
};

const ALLOWED_POSITIONS = ["GK","CB","LB","RB","LWB","RWB","CDM","CM","CAM","LM","RM","LW","RW","SS","ST"];
const POSITION_ALIASES = {
  "STRIKER": "ST", "FORWARD": "ST", "CENTRE FORWARD": "ST", "CENTER FORWARD": "ST",
  "GOALKEEPER": "GK", "KEEPER": "GK",
  "CENTRE BACK": "CB", "CENTER BACK": "CB", "CENTREBACK": "CB", "CENTRAL DEFENDER": "CB",
  "LEFT BACK": "LB", "RIGHT BACK": "RB",
  "LEFT WING BACK": "LWB", "RIGHT WING BACK": "RWB",
  "DEFENSIVE MIDFIELDER": "CDM", "DEFENSIVE MID": "CDM", "HOLDING MIDFIELDER": "CDM",
  "CENTRAL MIDFIELDER": "CM", "CENTRAL MID": "CM", "MIDFIELDER": "CM",
  "ATTACKING MIDFIELDER": "CAM", "ATTACKING MID": "CAM", "NUMBER 10": "CAM",
  "LEFT WINGER": "LW", "RIGHT WINGER": "RW", "WINGER": "RW",
  "LEFT MIDFIELDER": "LM", "RIGHT MIDFIELDER": "RM",
  "SECOND STRIKER": "SS",
};

const MAX_BYTES = 10 * 1024 * 1024;

// ----- Helpers -----
function parseDate(v) {
  if (!v && v !== 0) return null;
  if (v instanceof Date && !isNaN(v)) return v.toISOString().slice(0, 10);
  const s = String(v).trim();
  // YYYY-MM-DD
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  // DD/MM/YYYY or DD-MM-YYYY
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  // Excel serial
  if (typeof v === "number") {
    const dt = XLSX.SSF.parse_date_code(v);
    if (dt) return `${dt.y}-${String(dt.m).padStart(2, "0")}-${String(dt.d).padStart(2, "0")}`;
  }
  // Generic JS Date fallback
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

function parseNumber(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number") return v;
  const cleaned = String(v).replace(/[€£$,\s]/g, "").replace(/k$/i, "000");
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

function normalisePosition(raw) {
  if (!raw) return { value: null, autocorrected: false };
  const upper = String(raw).trim().toUpperCase();
  if (ALLOWED_POSITIONS.includes(upper)) return { value: upper, autocorrected: false };
  const alias = POSITION_ALIASES[upper];
  if (alias) return { value: alias, autocorrected: true, original: raw };
  return { value: null, autocorrected: false };
}

function ageYears(dobISO) {
  if (!dobISO) return null;
  const dob = new Date(dobISO);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
}

function validateRow(row) {
  const errors = [];
  const warnings = [];
  const corrections = [];

  if (!row.first_name?.trim()) errors.push({ field: "First name", message: "Required — missing" });
  if (!row.last_name?.trim()) errors.push({ field: "Last name", message: "Required — missing" });

  // DOB
  if (!row.date_of_birth) {
    errors.push({ field: "Date of birth", message: "Required — missing" });
  } else {
    const parsed = parseDate(row.date_of_birth);
    if (!parsed) {
      errors.push({ field: "Date of birth", message: `Cannot parse "${row.date_of_birth}" — use YYYY-MM-DD` });
    } else {
      if (parsed !== row.date_of_birth) corrections.push({ field: "Date of birth", original: row.date_of_birth, parsed });
      row.date_of_birth = parsed;
      const age = ageYears(parsed);
      if (age != null && (age < 14 || age > 50)) {
        errors.push({ field: "Date of birth", message: `Age ${age} is outside expected range 14–50` });
      }
    }
  }

  if (!row.nationality?.trim()) errors.push({ field: "Nationality", message: "Required — missing" });
  if (!row.current_club?.trim()) errors.push({ field: "Current club", message: "Required — missing" });

  // Position with fuzzy
  if (!row.position) {
    errors.push({ field: "Position", message: "Required — missing" });
  } else {
    const r = normalisePosition(row.position);
    if (r.value) {
      if (r.autocorrected) {
        warnings.push({ field: "Position", message: `"${r.original}" → "${r.value}" (auto-corrected)` });
        corrections.push({ field: "Position", original: r.original, parsed: r.value });
      }
      row.position = r.value;
    } else {
      errors.push({ field: "Position", message: `"${row.position}" is not in ${ALLOWED_POSITIONS.join(", ")}` });
    }
  }

  // Contract end
  if (!row.contract_end) {
    errors.push({ field: "Contract end date", message: "Required — missing" });
  } else {
    const parsed = parseDate(row.contract_end);
    if (!parsed) {
      errors.push({ field: "Contract end date", message: `Cannot parse "${row.contract_end}"` });
    } else {
      if (parsed !== row.contract_end) corrections.push({ field: "Contract end date", original: row.contract_end, parsed });
      row.contract_end = parsed;
    }
  }

  // Wage parsing
  if (row.weekly_gross_wage != null && row.weekly_gross_wage !== "") {
    const n = parseNumber(row.weekly_gross_wage);
    if (n == null) {
      warnings.push({ field: "Weekly gross wage", message: `Cannot parse "${row.weekly_gross_wage}" — will be skipped` });
      row.weekly_gross_wage = null;
    } else {
      if (typeof row.weekly_gross_wage !== "number") corrections.push({ field: "Weekly gross wage", original: row.weekly_gross_wage, parsed: n });
      row.weekly_gross_wage = n;
    }
  }

  // % fields — flag decimal form
  ["agent_commission_pct", "image_rights_pct"].forEach((f) => {
    if (row[f] != null && row[f] !== "") {
      const n = parseNumber(row[f]);
      if (n != null) {
        if (n > 0 && n < 1) {
          warnings.push({ field: f, message: `Value ${n} looks like a decimal — interpreting as ${(n * 100).toFixed(1)}%` });
        }
        row[f] = n;
      }
    }
  });

  // Number coercion for plain numbers
  ["height_cm", "weight_kg", "squad_number", "release_clause", "signing_fee"].forEach((f) => {
    if (row[f] != null && row[f] !== "") {
      const n = parseNumber(row[f]);
      if (n != null) row[f] = n;
    }
  });

  // Email format (warning, not error)
  if (row.player_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.player_email)) {
    warnings.push({ field: "Player email", message: `"${row.player_email}" is not a valid email — will be ignored` });
  }

  return { errors, warnings, corrections };
}

const STATUS_TONE = {
  ready: { label: "Ready", cls: "pm-pill-green", icon: CheckCircle2 },
  warning: { label: "Warning", cls: "pm-pill-amber", icon: AlertTriangle },
  error: { label: "Error", cls: "pm-pill-red", icon: AlertCircle },
  duplicate: { label: "Duplicate", cls: "pm-pill-amber", icon: AlertTriangle },
};

export default function BulkImportClients({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [rows, setRows] = useState([]); // [{ data, errors, warnings, corrections, status, skip, duplicate, expanded }]
  const [parseError, setParseError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const reset = () => {
    setStep(1); setFile(null); setParsing(false); setRows([]);
    setParseError(null); setBusy(false); setResult(null);
  };

  useEffect(() => { if (!open) setTimeout(reset, 300); }, [open]);

  // -------- Step 1: parse file --------
  const parseFile = useCallback(async (f) => {
    setParseError(null); setParsing(true);
    try {
      if (f.size > MAX_BYTES) throw new Error(`File is ${(f.size / 1024 / 1024).toFixed(1)}MB — maximum is 10MB. Try splitting into smaller batches.`);
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });

      // Wrong-template detection
      if (!wb.SheetNames.includes("Clients")) {
        if (wb.SheetNames.some((n) => /staff|medical|coach|scout/i.test(n))) {
          throw new Error("This looks like the staff template. Please use the client upload template.");
        }
        throw new Error("Could not find a 'Clients' sheet. Make sure you're using the Playmaker client upload template.");
      }
      const ws = wb.Sheets["Clients"];
      const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });
      if (!grid.length) throw new Error("Template is empty. Add your clients and re-upload.");

      const headerRow = grid[0].map((h) => String(h || "").trim());
      // Required headers present?
      const required = ["First name", "Last name", "Date of birth", "Nationality", "Position", "Current club", "Contract end date"];
      const missing = required.filter((h) => !headerRow.includes(h));
      if (missing.length) throw new Error(`Required column${missing.length > 1 ? "s" : ""} missing: ${missing.join(", ")}. Don't modify column headers.`);

      // Data rows start at index 2 (skip header row 0 + label row 1)
      const dataRows = grid.slice(2).filter((arr) => arr.some((c) => c !== "" && c != null));
      if (!dataRows.length) {
        setRows([]); setFile(f); setParsing(false);
        setParseError("Template is empty. Add your clients and re-upload.");
        return;
      }

      const parsedRows = dataRows.map((arr, idx) => {
        const obj = {};
        headerRow.forEach((header, colIdx) => {
          const key = CLIENT_COLUMN_MAP[header];
          if (key) obj[key] = arr[colIdx] === "" ? null : arr[colIdx];
        });
        const v = validateRow(obj);
        const status = v.errors.length ? "error" : v.warnings.length ? "warning" : "ready";
        return {
          row_number: idx + 1,
          data: obj,
          errors: v.errors,
          warnings: v.warnings,
          corrections: v.corrections,
          status,
          skip: false,
          duplicate_of: null,
          expanded: false,
        };
      });

      // Server-side dup-check
      try {
        const dupRes = await api.post("/import/clients/duplicate-check", {
          rows: parsedRows.map((r) => r.data),
        });
        const matches = dupRes.data?.matches || [];
        matches.forEach((m) => {
          if (m.existing) {
            parsedRows[m.index].duplicate_of = m.existing;
            if (parsedRows[m.index].status === "ready") parsedRows[m.index].status = "duplicate";
          }
        });
      } catch { /* ignore dup-check failure */ }

      setRows(parsedRows);
      setFile(f);
      setStep(2);
    } catch (e) {
      setParseError(e.message || "Could not parse file.");
      setFile(f);
    } finally { setParsing(false); }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setDrag(false);
    const f = e.dataTransfer.files?.[0]; if (f) parseFile(f);
  }, [parseFile]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0]; if (f) parseFile(f);
  };

  // -------- Step 2 actions --------
  const toggleSkip = (idx) => setRows((all) => all.map((r, i) => i === idx ? { ...r, skip: !r.skip } : r));
  const toggleExpanded = (idx) => setRows((all) => all.map((r, i) => i === idx ? { ...r, expanded: !r.expanded } : r));
  const editField = (idx, key, value) => setRows((all) => all.map((r, i) => {
    if (i !== idx) return r;
    const data = { ...r.data, [key]: value };
    const v = validateRow(data);
    const status = v.errors.length ? "error" : v.warnings.length ? "warning" : (r.duplicate_of ? "duplicate" : "ready");
    return { ...r, data, errors: v.errors, warnings: v.warnings, corrections: v.corrections, status };
  }));

  const summary = useMemo(() => {
    const active = rows.filter((r) => !r.skip);
    return {
      ready: active.filter((r) => r.status === "ready").length,
      warning: active.filter((r) => r.status === "warning").length,
      error: active.filter((r) => r.status === "error").length,
      duplicate: active.filter((r) => r.status === "duplicate").length,
      skipped: rows.filter((r) => r.skip).length,
      total: rows.length,
    };
  }, [rows]);

  const fieldCoverage = useMemo(() => {
    const labels = Object.entries(CLIENT_COLUMN_MAP);
    return labels.map(([label, key]) => ({
      label, key,
      filled: rows.filter((r) => r.data[key] != null && r.data[key] !== "").length,
    }));
  }, [rows]);

  const downloadErrorReport = () => {
    const failed = rows.filter((r) => r.status === "error" || r.errors.length);
    if (!failed.length) return;
    const csvHeader = ["Row", "Name", "Errors", "Warnings"].join(",");
    const lines = failed.map((r) => [
      r.row_number,
      `"${(r.data.first_name || "") + " " + (r.data.last_name || "")}"`,
      `"${r.errors.map((e) => `${e.field}: ${e.message}`).join("; ")}"`,
      `"${r.warnings.map((w) => `${w.field}: ${w.message}`).join("; ")}"`,
    ].join(","));
    const blob = new Blob([csvHeader + "\n" + lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "playmaker_import_errors.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // -------- Step 3: confirm + send --------
  const doImport = async () => {
    setBusy(true);
    try {
      const sendRows = rows.filter((r) => !r.skip && r.status !== "error").map((r) => r.data);
      const res = await api.post("/import/clients", {
        rows: sendRows,
        options: { skip_duplicates: false, merge_existing: false },
      });
      setResult(res.data);
      setStep(4);
      onSuccess?.(res.data);
    } catch (e) {
      setParseError(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/clients/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "playmaker_client_upload_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  if (!open) return null;

  const importable = summary.ready + summary.warning + summary.duplicate;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm" data-testid="bulk-import-modal">
      <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Bulk import</div>
            <h2 className="font-display text-xl font-black tracking-tight">Bulk import clients</h2>
          </div>
          <Stepper step={step} />
          <button
            onClick={downloadTemplate}
            className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5"
            data-testid="bulk-import-download-template"
          >
            <Download size={12} /> Download template
          </button>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 p-1" data-testid="bulk-import-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-auto pm-scroll p-6">
          {step === 1 && (
            <Step1
              file={file} parsing={parsing} parseError={parseError} drag={drag}
              setDrag={setDrag} onDrop={onDrop} fileInputRef={fileInputRef}
              onFileChange={onFileChange} onDownloadTemplate={downloadTemplate}
              onRemove={reset}
            />
          )}
          {step === 2 && (
            <Step2
              summary={summary} rows={rows} fieldCoverage={fieldCoverage}
              onToggleSkip={toggleSkip} onToggleExpanded={toggleExpanded}
              onEditField={editField} onDownloadErrorReport={downloadErrorReport}
            />
          )}
          {step === 3 && (
            <Step3 summary={summary} importable={importable} busy={busy} onBack={() => setStep(2)} onConfirm={doImport} />
          )}
          {step === 4 && result && (
            <Step4 result={result} onAnother={reset} onClose={onClose} />
          )}
        </div>

        {/* Footer */}
        {(step === 2 || step === 3) && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center gap-3">
            {step > 1 && step < 4 && (
              <button onClick={() => setStep((s) => Math.max(1, s - 1))} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2" data-testid="bulk-back-btn">
                <ChevronLeft size={14} /> Back
              </button>
            )}
            <div className="flex-1" />
            {step === 2 && (
              <button
                onClick={() => setStep(3)}
                disabled={importable === 0}
                className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-40"
                data-testid="bulk-continue-btn"
              >
                Continue · {importable} ready <ChevronRight size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ----- Steps ----- */

function Stepper({ step }) {
  const labels = ["Upload", "Review", "Confirm", "Done"];
  return (
    <div className="hidden md:flex items-center gap-2 text-[11px] font-bold" data-testid="bulk-stepper">
      {labels.map((l, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={l} className="flex items-center gap-2">
            <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[11px] ${
              done ? "bg-emerald-500 text-white" : active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"
            }`}>{done ? "✓" : idx}</span>
            <span className={active ? "text-slate-900" : "text-slate-500"}>{l}</span>
            {idx < labels.length && <span className="text-slate-300">›</span>}
          </div>
        );
      })}
    </div>
  );
}

function Step1({ file, parsing, parseError, drag, setDrag, onDrop, fileInputRef, onFileChange, onDownloadTemplate, onRemove }) {
  return (
    <div className="space-y-5">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        tabIndex={0}
        role="button"
        aria-label="Upload Playmaker client template"
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
        className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
          drag ? "border-emerald-500 bg-emerald-50/40" : "border-slate-300 bg-slate-50/40 hover:border-slate-400"
        }`}
        data-testid="bulk-upload-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={onFileChange}
          data-testid="bulk-file-input"
        />
        <div className="flex justify-center mb-3">
          <FileSpreadsheet size={42} className="text-slate-400" />
        </div>
        <div className="font-display text-lg font-bold tracking-tight">Drop your Playmaker template here</div>
        <div className="text-sm text-slate-500 mt-1">or click to browse</div>
        <div className="text-[11px] text-slate-400 mt-2 font-mono-tab">.xlsx · .xls · .csv · max 10 MB</div>
      </div>

      <div className="text-center text-sm">
        <span className="text-slate-500">Don't have the template? </span>
        <button onClick={onDownloadTemplate} className="font-bold text-emerald-700 hover:underline" data-testid="step1-download-template">
          Download client upload template →
        </button>
      </div>

      {file && (
        <div className="pm-card p-4 flex items-center gap-3" data-testid="bulk-selected-file">
          <FileSpreadsheet size={18} className="text-emerald-700" />
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate">{file.name}</div>
            <div className="text-[11px] text-slate-500 font-mono-tab">{(file.size / 1024).toFixed(1)} KB · {parsing ? "Parsing…" : "Ready"}</div>
          </div>
          <button onClick={onRemove} className="text-slate-400 hover:text-red-600" data-testid="bulk-remove-file"><Trash2 size={16} /></button>
        </div>
      )}

      {parseError && (
        <div className="pm-card p-4 flex items-start gap-3 border-l-4 border-l-red-400 bg-red-50/40" data-testid="bulk-parse-error">
          <FileX size={18} className="text-red-700 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-800">{parseError}</div>
        </div>
      )}
    </div>
  );
}

function Step2({ summary, rows, fieldCoverage, onToggleSkip, onToggleExpanded, onEditField, onDownloadErrorReport }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
      <div className="space-y-4 min-w-0">
        {/* Summary banner */}
        <div className="pm-card p-4 flex flex-wrap gap-3 items-center" data-testid="bulk-summary">
          <SummaryChip label="Ready" value={summary.ready} tone="green" />
          <SummaryChip label="Warnings" value={summary.warning} tone="amber" />
          <SummaryChip label="Duplicates" value={summary.duplicate} tone="amber" />
          <SummaryChip label="Errors" value={summary.error} tone="red" />
          <SummaryChip label="Skipped" value={summary.skipped} tone="slate" />
          <span className="ml-auto text-[11px] text-slate-500 font-mono-tab">{summary.total} rows total</span>
          {summary.error > 0 && (
            <button onClick={onDownloadErrorReport} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="bulk-download-errors">
              <Download size={12} /> Error report
            </button>
          )}
        </div>

        {/* Validation table */}
        <div className="pm-card overflow-hidden">
          <div className="overflow-auto pm-scroll max-h-[60vh]">
            <table className="w-full text-sm" data-testid="bulk-validation-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <Th>#</Th>
                  <Th>Status</Th>
                  <Th>Name</Th>
                  <Th>Position</Th>
                  <Th>Club</Th>
                  <Th>DOB</Th>
                  <Th>Issues</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const tone = STATUS_TONE[r.status];
                  const Icon = tone.icon;
                  return (
                    <>
                      <tr key={idx} className={`border-t border-slate-100 ${r.skip ? "opacity-40" : ""}`} data-testid={`bulk-row-${idx}`}>
                        <Td><span className="font-mono-tab text-xs text-slate-500">{r.row_number}</span></Td>
                        <Td>
                          <span className={`pm-pill ${tone.cls} text-[10px] inline-flex items-center gap-1`}>
                            <Icon size={10} /> {tone.label}
                          </span>
                        </Td>
                        <Td>
                          <div className="font-bold text-sm text-slate-900 truncate">{r.data.first_name} {r.data.last_name}</div>
                          {r.duplicate_of && <div className="text-[10px] text-amber-700">↺ existing: {r.duplicate_of.full_name} ({r.duplicate_of.current_club})</div>}
                        </Td>
                        <Td><span className="font-mono-tab text-xs">{r.data.position || "—"}</span></Td>
                        <Td><span className="text-xs truncate">{r.data.current_club || "—"}</span></Td>
                        <Td><span className="font-mono-tab text-xs">{r.data.date_of_birth || "—"}</span></Td>
                        <Td>
                          {(r.errors.length + r.warnings.length) > 0 ? (
                            <button onClick={() => onToggleExpanded(idx)} className="text-[11px] font-bold text-slate-600 hover:text-slate-900 underline" data-testid={`bulk-row-issues-${idx}`}>
                              {r.errors.length} error{r.errors.length !== 1 ? "s" : ""} · {r.warnings.length} warning{r.warnings.length !== 1 ? "s" : ""}
                            </button>
                          ) : (
                            <span className="text-[11px] text-emerald-700">—</span>
                          )}
                        </Td>
                        <Td>
                          <button onClick={() => onToggleSkip(idx)} className="text-[11px] font-bold text-slate-500 hover:text-red-700" data-testid={`bulk-row-skip-${idx}`}>
                            {r.skip ? "Include" : "Skip"}
                          </button>
                        </Td>
                      </tr>
                      {r.expanded && (r.errors.length + r.warnings.length) > 0 && (
                        <tr key={idx + "-x"} className="bg-amber-50/30">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {[...r.errors, ...r.warnings].map((iss, i) => (
                                <InlineFix key={i} row={r} issue={iss} idx={idx} onEditField={onEditField} />
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Sidebar: field coverage */}
      <div className="space-y-3 lg:sticky lg:top-0 self-start">
        <div className="pm-card p-4" data-testid="bulk-field-coverage">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2 flex items-center gap-1.5"><TableIcon size={11} /> Field coverage</div>
          <div className="space-y-1.5 max-h-[55vh] overflow-auto pm-scroll pr-1">
            {fieldCoverage.map((f) => {
              const pct = rows.length ? Math.round((f.filled / rows.length) * 100) : 0;
              return (
                <div key={f.key} className="text-[11px]">
                  <div className="flex justify-between items-baseline">
                    <span className="text-slate-700 truncate">{f.label}</span>
                    <span className="font-mono-tab text-slate-500">{f.filled}/{rows.length}</span>
                  </div>
                  <div className="h-1 bg-slate-100 rounded overflow-hidden mt-0.5">
                    <div className="h-full bg-emerald-400" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineFix({ row, issue, idx, onEditField }) {
  const fieldLabel = issue.field;
  const fieldKey = Object.entries(CLIENT_COLUMN_MAP).find(([h]) => h === fieldLabel)?.[1] || fieldLabel;
  const value = row.data[fieldKey] || "";
  return (
    <div className="border border-amber-200 rounded-lg p-2 bg-white text-[11px]">
      <div className="font-bold text-slate-800">{fieldLabel}</div>
      <div className="text-amber-700">{issue.message}</div>
      <input
        defaultValue={value}
        onBlur={(e) => { if (e.target.value !== value) onEditField(idx, fieldKey, e.target.value); }}
        className="pm-input mt-1 text-xs h-8"
        data-testid={`inline-fix-${idx}-${fieldKey}`}
        placeholder="Fix value…"
      />
    </div>
  );
}

function Step3({ summary, importable, busy, onBack, onConfirm }) {
  return (
    <div className="max-w-xl mx-auto py-4">
      <div className="pm-card p-8 text-center">
        <CheckCircle2 size={36} className="text-emerald-600 mx-auto" />
        <h3 className="font-display text-2xl font-black tracking-tight mt-3">Ready to import</h3>
        <div className="mt-6 space-y-2 text-sm text-slate-700 max-w-md mx-auto text-left">
          <Line label="Will be created" value={importable} tone="green" testid="confirm-import-count" />
          <Line label="Skipped (errors or you marked them)" value={summary.error + summary.skipped} tone="slate" />
          {summary.duplicate > 0 && <Line label="Possible duplicates — importing anyway" value={summary.duplicate} tone="amber" />}
        </div>
        <p className="text-xs text-slate-500 mt-6 leading-relaxed max-w-md mx-auto">
          Wyscout, SciSports, Transfermarkt, Gmail routing, WhatsApp / SMS, and Google Calendar will be connected
          for every row that has the required IDs / URLs (mocked).
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button onClick={onBack} className="pm-btn-secondary h-11 px-5 rounded-xl text-sm font-bold" data-testid="confirm-back">Back to review</button>
          <button
            onClick={onConfirm}
            disabled={busy || importable === 0}
            className="pm-btn-primary h-11 px-5 rounded-xl text-sm font-bold disabled:opacity-40"
            data-testid="confirm-import-btn"
          >
            {busy ? "Importing…" : `Import ${importable} clients`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Step4({ result, onAnother, onClose }) {
  if (result.rolled_back) {
    return (
      <div className="max-w-xl mx-auto py-4">
        <div className="pm-card p-8 text-center" data-testid="bulk-rollback">
          <AlertCircle size={36} className="text-red-600 mx-auto" />
          <h3 className="font-display text-2xl font-black tracking-tight mt-3">Import rolled back</h3>
          <p className="text-sm text-slate-700 mt-4 max-w-md mx-auto">{result.reason}</p>
          <div className="mt-6">
            <button onClick={onAnother} className="pm-btn-primary h-11 px-5 rounded-xl text-sm font-bold" data-testid="bulk-try-again">Try again</button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-2xl mx-auto py-4">
      <div className="pm-card p-8" data-testid="bulk-success">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><CheckCircle2 size={22} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">Success</div>
            <h3 className="font-display text-2xl font-black tracking-tight">{result.created} clients added to your roster</h3>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <Tile label="Created" value={result.created} />
          <Tile label="Skipped" value={result.skipped} />
          <Tile label="Errors" value={result.errors?.length || 0} />
        </div>
        {result.warnings?.length > 0 && (
          <div className="mt-5" data-testid="bulk-warnings-section">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-2 flex items-center gap-1.5">
              <AlertTriangle size={11} /> Heads-up · {result.warnings.length} non-blocking warning{result.warnings.length === 1 ? "" : "s"}
            </div>
            <div className="pm-card border-amber-200 bg-amber-50/40 divide-y divide-amber-100">
              {result.warnings.slice(0, 8).map((w, i) => (
                <div key={i} className="px-3 py-2 text-[12px] text-slate-700" data-testid={`bulk-warning-${i}`}>
                  <span className="font-bold">{w.name}</span>
                  {w.field ? <span className="text-slate-500"> · {w.field}</span> : null}
                  <span className="text-slate-600"> — {w.warning}</span>
                </div>
              ))}
              {result.warnings.length > 8 && (
                <div className="px-3 py-2 text-[11px] text-slate-500 italic">+ {result.warnings.length - 8} more — see audit log</div>
              )}
            </div>
          </div>
        )}
        {result.sync?.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Integrations connected</div>
            <div className="pm-card divide-y divide-slate-100">
              {result.sync.slice(0, 6).map((s, i) => (
                <div key={i} className="p-3 flex items-center gap-2 text-sm" data-testid={`sync-${i}`}>
                  <span className="font-bold flex-1 truncate">{s.name}</span>
                  <Pill on={s.wyscout} label="Wyscout" />
                  <Pill on={s.scisports} label="SciSports" />
                  <Pill on={s.transfermarkt} label="Transfermarkt" />
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-6 flex items-center gap-3">
          <button onClick={onAnother} className="pm-btn-secondary h-11 px-5 rounded-xl text-sm font-bold" data-testid="bulk-import-another">Import another file</button>
          <button onClick={onClose} className="pm-btn-primary h-11 px-5 rounded-xl text-sm font-bold flex-1" data-testid="bulk-view-clients">View all clients</button>
        </div>
      </div>
    </div>
  );
}

/* ----- shared bits ----- */
function Th({ children }) { return <th className="text-left text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 px-4 py-3">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }
function SummaryChip({ label, value, tone }) {
  const cls = { green: "pm-pill-green", amber: "pm-pill-amber", red: "pm-pill-red", slate: "pm-pill-slate" }[tone];
  return <span className={`pm-pill ${cls} text-[11px] inline-flex items-center gap-1.5`} data-testid={`summary-${tone}`}>{label} <b className="font-mono-tab">{value}</b></span>;
}
function Line({ label, value, tone, testid }) {
  const dot = tone === "green" ? "bg-emerald-500" : tone === "amber" ? "bg-amber-500" : "bg-slate-400";
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-2" data-testid={testid}>
      <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${dot}`} />{label}</span>
      <span className="font-mono-tab font-bold">{value}</span>
    </div>
  );
}
function Tile({ label, value }) {
  return (
    <div className="pm-card p-4 text-center">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
      <div className="font-display text-3xl font-black mt-1">{value}</div>
    </div>
  );
}
function Pill({ on, label }) {
  return <span className={`pm-pill text-[10px] ${on ? "pm-pill-green" : "pm-pill-slate"}`}>{label}{on ? " ✓" : ""}</span>;
}
